import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Page, PageHeader } from '../components/layout/AppShell';
import { Card } from '../components/ui/Card';
import { DataTable, TableToolbar, Pagination } from '../components/ui/DataTable';
import { AsyncBoundary, EmptyState, SkeletonRows } from '../components/ui/AsyncBoundary';
import { SegmentedControl, Button } from '../components/ui/Button';
import { Select, Textarea } from '../components/ui/Field';
import { Modal, Tabs, useToast } from '../components/ui/Overlay';
import { StatTile } from '../components/charts/StatTile';
import { AlertRulesPanel } from './AlertRules';
import { alertColumns } from '../components/domain/columns';
import { alertTitle } from '../lib/domain';
import { useResource, useMutation, usePagedList } from '../hooks/useResource';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../lib/api';

/**
 * The alert inbox.
 *
 * Acknowledge and resolve are two steps on purpose: acknowledging says
 * "someone has seen this", resolving says "it is dealt with". Collapsing them
 * loses the distinction that makes an inbox useful during an incident.
 */
export function Alerts() {
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();

  const [tab, setTab] = useState('inbox');
  const [state, setState] = useState('OPEN');
  const [severity, setSeverity] = useState('');
  const [acknowledging, setAcknowledging] = useState(null);
  const [note, setNote] = useState('');

  const alerts = usePagedList(
    '/api/alerts',
    { state: state || undefined, severity: severity || undefined, open: 'false' },
    { limit: 50, pollMs: 30_000 }
  );

  const acknowledge = useMutation(async (alert, resolve) => {
    await api.post(`/api/alerts/${alert.id}/ack`, { resolve, note: note || undefined });
  });

  // The tiles count the page, except the total, which is the server's — a
  // dashboard that says "12 open" because twelve fitted on this page is worse
  // than no number at all.
  const counts = useMemo(() => {
    const rows = alerts.rows;
    return {
      total: alerts.total,
      critical: rows.filter((a) => a.severity === 'CRITICAL' && a.state !== 'RESOLVED').length,
      open: rows.filter((a) => a.state === 'OPEN').length,
      acknowledged: rows.filter((a) => a.state === 'ACKNOWLEDGED').length,
    };
  }, [alerts.rows, alerts.total]);

  const submit = async (resolve) => {
    try {
      await acknowledge.run(acknowledging, resolve);
      toast.success(resolve ? 'Alert resolved.' : 'Alert acknowledged.');
      setAcknowledging(null);
      setNote('');
      alerts.refetch();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Page className="space-y-4">
      <PageHeader title="Alerts" subtitle="Everything the platform has raised in your scope" />

      {/* Rules are where alerts come from, so they belong next to the inbox
          rather than buried in settings — the usual reason to open this page a
          second time is that a threshold was wrong. */}
      <Tabs
        tabs={[
          { value: 'inbox', label: 'Inbox', icon: 'bell' },
          // Reading the rule list needs the same permission as writing one, so
          // a viewer is never offered a tab that would answer 403.
          ...(can('alert:rules') ? [{ value: 'rules', label: 'Rules', icon: 'shield' }] : []),
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === 'rules' ? <AlertRulesPanel /> : (
      <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Matching" value={counts.total} icon="bell" tone="brand" compact />
        <StatTile label="Critical" value={counts.critical} icon="critical" tone="critical" compact />
        <StatTile label="Open" value={counts.open} icon="warning" tone="warning" compact />
        <StatTile label="Acknowledged" value={counts.acknowledged} icon="check" tone="good" compact />
      </div>

      <Card className="overflow-hidden">
        <TableToolbar>
          <SegmentedControl
            value={state}
            onChange={setState}
            options={[
              { value: 'OPEN', label: 'Open' },
              { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
              { value: 'RESOLVED', label: 'Resolved' },
              { value: '', label: 'All' },
            ]}
          />
          <Select
            size="sm"
            value={severity}
            onChange={(event) => setSeverity(event.target.value)}
            placeholder="Any severity"
            className="w-40"
            options={[
              { value: 'CRITICAL', label: 'Critical' },
              { value: 'WARNING', label: 'Warning' },
              { value: 'INFO', label: 'Info' },
            ]}
          />
          <Button variant="ghost" size="sm" icon="refresh" onClick={alerts.refetch} className="ml-auto">
            Refresh
          </Button>
        </TableToolbar>

        <AsyncBoundary
          state={alerts}
          skeleton={<SkeletonRows rows={8} />}
          isEmpty={() => !alerts.rows.length}
          empty={
            <EmptyState
              icon="check"
              title={state === 'OPEN' ? 'Nothing open' : 'No alerts match'}
              description={
                state === 'OPEN'
                  ? 'Every alert in your scope has been dealt with.'
                  : 'Try a different state or severity.'
              }
            />
          }
        >
          {() => (
            <>
              <DataTable
                columns={alertColumns({
                  onAcknowledge: can('alert:ack') ? setAcknowledging : undefined,
                })}
                rows={alerts.rows}
                onRowClick={(row) => row.vehicle_id && navigate(`/vehicles/${row.vehicle_id}`)}
                defaultSort={{ key: 'opened_at', direction: 'desc' }}
                paged={alerts.total > alerts.page.limit}
              />
              <Pagination page={alerts.page} onChange={alerts.setOffset} unit="alerts" />
            </>
          )}
        </AsyncBoundary>
      </Card>

      <Modal
        open={Boolean(acknowledging)}
        onClose={() => {
          setAcknowledging(null);
          setNote('');
        }}
        title={acknowledging ? alertTitle(acknowledging) : ''}
        description={acknowledging?.message}
        footer={
          <>
            <Button
              onClick={() => {
                setAcknowledging(null);
                setNote('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              loading={acknowledge.pending}
              onClick={() => submit(false)}
              disabled={acknowledging?.state !== 'OPEN'}
            >
              Acknowledge
            </Button>
            <Button variant="primary" loading={acknowledge.pending} onClick={() => submit(true)}>
              Resolve
            </Button>
          </>
        }
      >
        <p className="mb-3 text-[13px] text-ink-2">
          Acknowledging records that someone has seen this. Resolving closes it — the same condition
          can then raise a new alert.
        </p>
        <Textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="What did you find? (optional)"
        />
      </Modal>
      </>
      )}
    </Page>
  );
}
